import * as React from "react";
import { useEffect, useState, useMemo, useRef } from "react";
import EYLogoAndHelpUrl from "../EYLogoAndHelpUrl";
import { screenToRender } from "../../util/UIConstants";
import styled from "styled-components";
import { labels } from "../../util/utils";
import { UniversalService } from "../../services/universal/UniversalService";
import MotifButton from "@ey/motif-react-wrapper/Button";
import MotifTypeahead from "@ey/motif-react-wrapper/Typeahead";
import MotifFormField from "@ey/motif-react-wrapper/FormField";
import MotifLabel from "@ey/motif-react-wrapper/Label";
import ErrorSummary from "./error/ErrorSummary";
import ProgressLoader from "./progressLoader";
import { parseErrorData } from "../../helpers/common/DataUtilityHelper";
import { MotifAvIcPlayArrow24px } from "@ey-xd/motif-icon";
import { MotifIcon } from "@ey-xd/motif-react";
import { EndPoint } from "../../util";
import MotifErrorMessage from "@ey/motif-react-wrapper/ErrorMessage";

function EngagementSelectLayout(props: any) {
  const {
    setStepToRender,
    setEngagementIdToRender,
    engagementNameAndId,
    setEngagementNameAndId,
    toastErrorMessage,
    resetErrorMessage,
    setLdcVersioning,
    engagementId,
  } = props;
  const [loaderState, setLoaderState] = useState(true);
  const [engagementDetails, setEngagementDetails] = useState<any>([]);
  const [engagementNotSelected, setEngagementNotSelected] = useState({ error: false, errorMessage: "" });
  const [errorMessage, setErrorMessage] = useState(toastErrorMessage);
  const [renderScreen, setRenderScreen] = useState(false);
  const typeaheadRef = useRef<any>(null);

  const options = useMemo(() => {
    return engagementDetails.map((item) => `${item.engagementdescription.trim()} (${item.engagementid})`);
  }, [engagementDetails]);

  const currentEngagementDetails = useMemo(() => {
    return engagementDetails.find((item) => item.engagementid.toString() === engagementId);
  }, [engagementDetails, engagementId]);

  useEffect(() => {
    getEngagementUserDetails().then(() => {
      setRenderScreen(true);
    });
  }, []);

  const onSelectingEngagementDetails = (data) => {
    if (data) {
      data = data.toString().trim();
      let match = data.match(/\(([^)]+)\)(?!.*\(([^)]+)\))/);
      let engagementID = match ? match[1] : "";
      setEngagementNameAndId(data);
      setEngagementIdToRender(engagementID);
      setEngagementNotSelected({ error: false, errorMessage: "" });
    } else {
      setEngagementNameAndId("");
      setEngagementIdToRender("");
    }
  };

  const generateErrorMessage = () => {
    // we can some specfic validation error
    return labels.engagementIdNotFound;
    // create error based on validation response from api such as precondition failed, bad request
  };

  // Define the template component
  const EngagementTemplate = ({ value }) => <span title={value}>{value} </span>;

  const renderEngagementSelectDropdown = useMemo(() => {
    if (renderScreen) {
      return (
        <MotifFormField className="eng-typeahead-search">
          <MotifLabel position="in" aria-label={labels.eyCanvasEngagement}>
            {labels.eyCanvasEngagement}
          </MotifLabel>
          <MotifTypeahead
            ref={typeaheadRef}
            aria-label="Search"
            onChange={(e) => onSelectingEngagementDetails(e.target.value)}
            id="engagementDetails"
            template={EngagementTemplate}
            placeholder=" "
            items={options}
            value={engagementNameAndId}
            onSelect={(val) => {
              onSelectingEngagementDetails(val);
            }}
            aria-describedby="search-error"
            labelPosition="in"
            visibleOptions={4}
            icons={{ prefix: null }}
            title={engagementNameAndId}
            noResultsText={labels.noRecordsFound}
            onClose={() => {
              if (typeaheadRef.current) {
                typeaheadRef.current.blur(); // Remove focus from the component
              }
            }}
          ></MotifTypeahead>
          {engagementNotSelected.error && (
            <MotifErrorMessage id="select-error-reactive">{engagementNotSelected.errorMessage}</MotifErrorMessage>
          )}
        </MotifFormField>
      );
    }
  }, [renderScreen, engagementNotSelected, engagementNameAndId]);

  const onClick: React.MouseEventHandler<HTMLButtonElement> = async () => {
    if (!engagementNameAndId) {
      setEngagementNotSelected({ error: true, errorMessage: labels.engagementNotSelected });
      return;
    }

    if (engagementNameAndId && !options.find((item) => item === engagementNameAndId)) {
      setEngagementNotSelected({ error: true, errorMessage: labels.invalidEngagement });
      return;
    }
    setLoaderState(true);
    let herculesApiBaseUriAsync: EndPoint = await UniversalService.getHerculesApiBaseUriAsync(engagementId);
    if (herculesApiBaseUriAsync?.ErrorCode || !herculesApiBaseUriAsync?.datacenterversion) {
      setLoaderState(false);
      setErrorMessage(labels.apiServerError);
      return;
    }
    setLdcVersioning({
      isLdcUpgraded: herculesApiBaseUriAsync?.isldcupgraded,
      datacenterversion: herculesApiBaseUriAsync?.datacenterversion,
    });
    setLoaderState(false);
    setStepToRender(screenToRender.EntitySelectLayout);
  };

  const getEngagementUserDetails = async () => {
    let engagementDetails: any[] = [];

    if (!errorMessage) {
      let userData = await UniversalService.getUserAccessibleEngagement();
      let errorMessage = parseErrorData(userData, generateErrorMessage);
      if (errorMessage) {
        setLoaderState(false);
        setErrorMessage(errorMessage);
        return;
      }

      if (Array.isArray(userData?.response.data.engagementmetadatadetails)) {
        engagementDetails.push(...userData?.response.data.engagementmetadatadetails);
      }

engagementDetails.sort((a, b) => a.engagementdescription.localeCompare(b.engagementdescription));

      setLoaderState(false);
      setEngagementDetails(engagementDetails);
    }
  };

  const getEngagementSelectionLayout = () => {
    return (
      <section className="body-wrap edit-doc-wrap">
        <section className="body-top-wrap">
          <EYLogoAndHelpUrl engagementId={engagementId} uri={currentEngagementDetails?.uri} />
          <section className="info-text-wrapper">
            <span className="informational-text">{labels.informationText}</span>
            <ul>
              <li>
                <MotifIcon iconFunction={MotifAvIcPlayArrow24px} />
                <span className="info-text-1">{labels.infoText1}</span>
              </li>

              <li>
                <MotifIcon iconFunction={MotifAvIcPlayArrow24px} />
                <span className="info-text-3">{labels.infoText3}</span>
              </li>
            </ul>
          </section>
          {<div>{renderEngagementSelectDropdown}</div>}
        </section>

        <section className="footer-wrap">
          <MotifButton
            className={"btn active-btn"}
            onClick={(e) => onClick(e)}
            size="small"
            type="submit"
            variant="primary-alt"
            //disabled={this.state.engagementIdToUse === "" ? true : false}
          >
            {labels.Continue}
          </MotifButton>
        </section>

        {errorMessage && (
          <ErrorSummary
            message={errorMessage}
            callBack={() => {
              setErrorMessage("");
              resetErrorMessage();
            }}
          ></ErrorSummary>
        )}
      </section>
    );
  };

  const renderContent = () => {
    if (loaderState) {
      return (
        <StyledMainSection className="main-wrap spinner-main">
          <section className="body-wrap">
            <section className="body-top-wrap">
              <EYLogoAndHelpUrl engagementId={engagementId} />
              <ProgressLoader show variant="default" />
            </section>
          </section>
        </StyledMainSection>
      );
    }

    return <StyledMainSection className="main-wrap">{getEngagementSelectionLayout()}</StyledMainSection>;
  };

  return renderContent();
}

export default EngagementSelectLayout;

const StyledMainSection = styled.section`
  height: 100%;
  &.spinner-main {
    background-color: var(--neutrals-900);
  }
  .body-wrap {
    position: relative;
    flex-flow: column;
    display: flex;
    background-color: var(--neutrals-50);
    height: 100vh;
    overflow: auto;
    justify-content: space-between;
    .error-wrapper {
      height: var(--px-22);
    }
    .body-top-wrap {
      padding: var(--spacing-medium) var(--spacing-x-large) var(--spacing-x-large) var(--spacing-x-large);
      .motif-select-input-text {
        width: calc(100% - var(--px-55)) !important;
      }
      .eng-typeahead-search {
        .motif-input {
          font-size: var(--px-12);
        }
        .motif-input-has-value {
          text-overflow: ellipsis;
          white-space: nowrap;
          overflow: hidden !important;
          padding-right: var(--px-40);
        }

        .motif-typeahead-results {
          max-height: var(--px-154);
          height: unset !important;
          .motif-typeahead-no-results {
            padding: var(--spacing-small) var(--spacing-medium);
            line-height: var(--px-18);
            font-size: var(--px-12);
            font-weight: 300;
          }
        }

        .motif-typeahead-results ul li button {
          line-height: var(--px-18);
          padding: var(--spacing-small) var(--spacing-medium);
          span {
            width: 100%;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
            display: inline-block;
            font-size: var(--px-12);
          }
        }
      }
    }
    .info-text-wrapper {
      display: flex;
      flex-direction: column;
      font-size: var(--px-14);
      font-weight: 300;
      color: var(--neutrals-900);
      line-height: var(--px-18);
      margin-bottom: var(--spacing-medium);
      ul {
        padding: 0;
        margin: 0;
        font-size: var(--px-12);
        li {
          list-style: none;
          margin: 0;
          display: flex;
          .info-text-2 {
            margin-left: var(--px-21);
          }
          .motif-icon {
            margin-right: var(--px-4);
            margin-left: var(--px-5);
            svg {
              fill: var(--neutrals-900);
              height: var(--px-10);
              width: var(--px-10);
            }
          }
        }
      }
    }
    .breadcrumb-wrap {
      display: flex;
      align-items: center;
      a {
        color: var(--brand-500) !important;
        text-decoration: none;
      }
      .chevron-right-icon {
        display: flex;
        fill: var(--brand-500);
        width: var(--px-16);
        height: var(--px-16);
        margin: var(--px-3) var(--px-8) 0 var(--px-8);
        svg {
          width: 100%;
          height: 100%;
        }
      }
    }

    .ul-heading {
      margin-top: var(--px-14) !important;
    }
    .unordered-list {
      padding: 0 0 0 var(--px-4);
      margin-bottom: var(--px-35);
      li {
        margin: 0;
        padding: 0;
        display: flex;
        align-items: flex-start;
        list-style: none;
        font-size: var(--px-12);
        font-weight: 300;
        margin-bottom: var(--px-10);
        svg {
          width: var(--px-10);
          height: var(--px-10);
          fill: var(--neutrals-900);
          margin-right: var(--px-5);
          margin-top: var(--px-3);
        }
        span {
          display: inline-block;
          width: calc(100% - var(--px-15));
        }
      }
    }
    .informational-text {
      font-size: var(--px-12);
      font-weight: 300;
      margin: 0;
      color: var(--neutrals-900);
    }
    .submit-text {
      font-weight: 400;
    }

    .informational-text2 {
      margin-top: var(--px-15);
      color: var(--neutrals-900);
    }

    /* .motif-label,
    .motif-select-search-input {
      font-size: var(--px-12);
    } */

    .eng-label {
      /* font-size: var(--px-12);
      font-weight: 400; */
      margin-bottom: var(--px-8);
    }

    .input-field {
      border-radius: var(--px-4);
      padding: var(--px-8) var(--px-5);
      border: 0;
      margin-bottom: var(--px-10);
      &:focus {
        outline: none;
      }
    }

    &.edit-doc-wrap {
      .select-option-wrap {
        span {
          text-overflow: ellipsis;
          overflow: hidden;
          white-space: nowrap;
          width: 100%;
          display: inline-block;
          font-size: var(--px-12);
        }
      }
      .info-text-1,
      .info-text-3 {
        width: calc(100% - var(--px-16));
        font-size: var(--px-12);
        font-weight: 300;
      }
    }
    .progress-bar {
      height: var(--px-10);
      background-color: #0d6f2f;
      overflow: hidden;
    }
    .footer-wrap {
      display: flex;
      justify-content: flex-end;
      padding: var(--spacing-large) var(--spacing-xx-large);
      border-top: 1px solid var(--neutrals-100);
    }
  }
  .motif-select-input-text {
    font-size: var(--px-12);
  }
`;
